import { apiFetch } from '../../../lib/http';

/**
 * API service for V2 Adaptive Learning endpoints.
 * Base path: /api/learn/adaptive
 */
export const adaptiveApi = {
  /**
   * Get V2 adaptive learning status.
   * @returns {Promise<{ v2Enabled: boolean }>}
   */
  getStatus({ auth }) {
    return apiFetch('/api/learn/adaptive/status', {
      method: 'GET',
      auth,
    });
  },

  /**
   * Enable V2 adaptive learning.
   * @returns {Promise<{ v2Enabled: true, message: string }>}
   */
  enable({ auth }) {
    return apiFetch('/api/learn/adaptive/enable', {
      method: 'POST',
      auth,
    });
  },

  /**
   * Disable V2 adaptive learning.
   * @returns {Promise<{ v2Enabled: false, message: string }>}
   */
  disable({ auth }) {
    return apiFetch('/api/learn/adaptive/disable', {
      method: 'POST',
      auth,
    });
  },

  /**
   * Get topic mastery data for the user.
   * @returns {Promise<{
   *   v2Enabled: boolean,
   *   topics: Array<{
   *     topicId: string,
   *     masteryScore: number,
   *     masteryLevel: 'Beginning' | 'Emerging' | 'Developing' | 'Proficient' | 'Mastered',
   *     confidence: number,
   *     isReliable: boolean,
   *     totalInteractions: number,
   *     lastInteractionAt: string | null
   *   }>
   * }>}
   */
  getMastery({ auth }) {
    return apiFetch('/api/learn/adaptive/mastery', {
      method: 'GET',
      auth,
    });
  },

  /**
   * Get adaptive parameters for a specific topic.
   * @param {{ topicId: string, auth: boolean }} params
   * @returns {Promise<{
   *   v2Enabled: boolean,
   *   difficulty: number,
   *   mastery: number,
   *   masteryLevel: string,
   *   confidence: number,
   *   dueForReview: boolean,
   *   relatedTopics: string[]
   * }>}
   */
  getParams({ topicId, auth }) {
    return apiFetch(`/api/learn/adaptive/params?topicId=${encodeURIComponent(topicId)}`, {
      method: 'GET',
      auth,
    });
  },

  /**
   * Get the review queue (items due for spaced repetition review).
   * @param {{ limit?: number, auth: boolean }} params
   * @returns {Promise<{
   *   v2Enabled: boolean,
   *   items: Array<{
   *     conceptId: string,
   *     courseId: string | null,
   *     conceptName: string | null,
   *     retrievability: number,
   *     status: 'Fresh' | 'Strong' | 'Fading' | 'Weak' | 'Forgotten',
   *     priority: number,
   *     lastReviewAt: string | null,
   *     nextReviewAt: string | null
   *   }>,
   *   totalDue: number
   * }>}
   */
  getReviewQueue({ limit = 20, auth }) {
    return apiFetch(`/api/learn/adaptive/review-queue?limit=${limit}`, {
      method: 'GET',
      auth,
    });
  },

  /**
   * Submit a review grade for a concept.
   * @param {{ conceptId: string, grade: 'AGAIN' | 'HARD' | 'GOOD' | 'EASY', auth: boolean }} params
   * @returns {Promise<{
   *   conceptId: string,
   *   newStability: number,
   *   nextReviewAt: string,
   *   repetitionCount: number
   * }>}
   */
  submitReview({ conceptId, grade, auth }) {
    return apiFetch(`/api/learn/adaptive/review/${encodeURIComponent(conceptId)}`, {
      method: 'POST',
      auth,
      body: { grade },
    });
  },
};
